import { useState, useEffect } from "react";
import { Bell, LogIn, Calendar, Building, Clock, Pin } from "lucide-react";
import {
  fetchOrgInfo,
  fetchPublicNotice,
  fetchUpcomingCampaign,
  STORAGE_KEYS,
} from "../../services/api";
import Select from "react-select";
import LogoNRCS from "../../../assets/images/LogoNRCS.png";
import { useNavigate } from "react-router-dom";

// Notice Card Component
const NoticeCard = ({ notice }) => {
  console.log("Rendering notice:", notice);
  return (
    <div className="card p-4 hover:shadow-medium transition-shadow duration-200 mb-4">
      <div className="flex items-start">
        <div className="p-2 rounded-lg bg-primary-100">
          <Bell className="h-5 w-5 text-primary-600" />
        </div>
        <div className="ml-4 flex-1">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">
            {notice.name ||
              notice.Name ||
              notice.title ||
              notice.Title ||
              "Untitled Notice"}
          </h3>
          <p className="text-sm text-gray-600">
            {notice.description ||
              notice.Description ||
              "No description available"}
          </p>
          {notice.Created && (
            <p className="text-xs text-gray-400 mt-2">
              {new Date(notice.Created).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

function PublicDashboard() {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [noticeData, setNoticeData] = useState([]);
  const [upcomingCampaigns, setUpcomingCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noticesLoading, setNoticesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userType, setUserType] = useState({ isBDO: false, isHospital: false });

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    console.log("Selected organization ID changed:", selectedOrgId);
    loadNotices();
    loadUpcomingCampaigns();
  }, [selectedOrgId]);

  const loadOrganizations = async () => {
    try {
      const orgs = await fetchOrgInfo();
      console.log("Fetched organizations:", orgs);

      // Transform the org data to match the expected format for the Select component
      const formattedOrgs = orgs
        .map((org) => {
          // Use ad_org_id as the primary ID field
          const orgId = org.ad_org_id || org.id || org.c_bpartner_id;
          const orgName = org.name || org.Name || "Unnamed Organization";

          if (!orgId) {
            console.warn("Organization has no valid ID:", org);
          }

          return {
            value: String(orgId), // Ensure value is a string for consistency
            label: orgName,
            id: String(orgId), // Ensure ID is a string for consistency
            Name: orgName,
            originalData: org, // Keep original data for debugging
          };
        })
        .filter((org) => org.id); // Filter out any organizations without an ID

      console.log("Formatted organizations:", formattedOrgs);
      setOrganizations(formattedOrgs);

      // Load last selected organization ID from localStorage
      const lastSelectedOrgId = localStorage.getItem(
        STORAGE_KEYS.LAST_SELECTED_ORG_ID
      );

      if (formattedOrgs.length > 0) {
        if (lastSelectedOrgId) {
          // Check if the last selected org exists in the current list
          const foundOrg = formattedOrgs.find(
            (org) => String(org.id) === lastSelectedOrgId
          );
          if (foundOrg) {
            setSelectedOrgId(foundOrg.id);
            return;
          }
        }
        // If no saved org or saved org not found, select the first one
        setSelectedOrgId(formattedOrgs[0].id);
        localStorage.setItem(
          STORAGE_KEYS.LAST_SELECTED_ORG_ID,
          String(formattedOrgs[0].id)
        );
      }
    } catch (error) {
      console.error("Error loading organizations:", error);
      setError("Failed to load organizations. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const loadNotices = async () => {
    if (!selectedOrgId) {
      console.log("No organization selected, skipping notice fetch");
      setNoticeData([]);
      return;
    }

    try {
      setNoticesLoading(true);
      // Ensure we're using the numeric ID for the API call
      const orgId = parseInt(selectedOrgId, 10);
      console.log(
        "Fetching public notices for org ID:",
        orgId,
        "(parsed from:",
        selectedOrgId,
        ")"
      );

      const notices = await fetchPublicNotice(orgId);
      console.log("Raw notices response:", notices);

      // Handle different response formats
      let noticesArray = [];
      if (Array.isArray(notices)) {
        noticesArray = notices;
      } else if (notices && typeof notices === "object") {
        noticesArray = notices.records || Object.values(notices);
      }

      // Filter out any invalid notices
      const validNotices = noticesArray.filter(
        (notice) =>
          notice && (notice.name || notice.Name || notice.title || notice.Title)
      );

      console.log("Processed notices array:", validNotices);
      setNoticeData(validNotices);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch public notices:", err);
      setError("Failed to load notices. Please try again later.");
      setNoticeData([]);
    } finally {
      setNoticesLoading(false);
    }
  };

  const loadUpcomingCampaigns = async () => {
    if (!selectedOrgId) {
      console.log(
        "No organization selected, skipping upcoming campaigns fetch"
      );
      setUpcomingCampaigns([]);
      return;
    }

    try {
      // Ensure we're using the numeric ID for the API call
      const orgId = parseInt(selectedOrgId, 10);
      console.log(
        "Fetching upcoming campaigns for org ID:",
        orgId,
        "(parsed from:",
        selectedOrgId,
        ")"
      );

      const campaigns = await fetchUpcomingCampaign(orgId);
      console.log("Raw upcoming campaigns response:", campaigns);

      // Handle different response formats
      let campaignsArray = [];
      if (Array.isArray(campaigns)) {
        campaignsArray = campaigns;
      } else if (campaigns && typeof campaigns === "object") {
        campaignsArray = campaigns.records || Object.values(campaigns);
      }

      // Filter out any invalid campaigns - campaigns should have at least a venue or startdate
      const validCampaigns = campaignsArray.filter(
        (campaign) => campaign && (campaign.venue || campaign.startdate)
      );

      console.log("Processed campaigns array:", validCampaigns);
      setUpcomingCampaigns(validCampaigns);
    } catch (err) {
      console.error("Failed to fetch upcoming campaigns:", err);
      setUpcomingCampaigns([]);
    }
  };

  const handleOrgChange = (selectedOption) => {
    if (selectedOption) {
      const orgId = selectedOption.value;
      setNoticeData([]);
      setSelectedOrgId(orgId);

      try {
        localStorage.setItem(STORAGE_KEYS.LAST_SELECTED_ORG_ID, String(orgId));
        console.log("Saved selected organization ID:", orgId);
      } catch (e) {
        console.error("Failed to save selected organization ID", e);
      }

      // The useEffect will trigger loadNotices automatically
    } else {
      console.log("No organization selected");
      setSelectedOrgId(null);
      setNoticeData([]);
      try {
        localStorage.removeItem(STORAGE_KEYS.LAST_SELECTED_ORG_ID);
      } catch (e) {
        console.error("Failed to clear organization ID from storage", e);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-6 max-w-md mx-auto">
          <div className="bg-red-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Bell className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Data
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadOrganizations}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left Side - Branding (fixed on md+) */}
      <div className="hidden md:flex md:w-1/2 h-screen fixed left-0 top-0 bg-gradient-to-br from-pink-50 to-white z-10">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-20 left-20 w-8 h-8 bg-pink-400 rounded-lg transform rotate-45"></div>
          <div className="absolute top-40 right-32 w-6 h-6 bg-pink-300 rounded-lg transform rotate-45"></div>
          <div className="absolute bottom-32 left-32 w-10 h-10 bg-pink-500 rounded-lg transform rotate-45"></div>
          <div className="absolute bottom-20 right-20 w-4 h-4 bg-pink-400 rounded-lg transform rotate-45"></div>
        </div>
        {/* Centered Content */}
        <div className="relative z-10 flex flex-col justify-center items-center w-full h-full text-center">
          {/* Large Logos */}
          <div className="mb-6 flex items-center justify-center">
            <img
              src={LogoNRCS}
              alt="BTSM Logo"
              className="h-16 md:h-24 w-auto"
            />
          </div>
          {/* Brand Text */}
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 tracking-tight">
              Blood Transfusion Service Management
            </h3>
            {/* <p className="text-base md:text-xl text-gray-600 leading-relaxed max-w-xs md:max-w-sm mx-auto">
              Blood Transfusion Service Management
            </p> */}
          </div>
        </div>
      </div>

      {/* Right Side - Content (scrollable) */}
      <div className="md:ml-[50%] w-full md:w-1/2 flex flex-col items-center bg-white px-6 py-8 sm:px-8 md:px-16 lg:px-24 md:py-12 min-h-screen overflow-y-auto">
        <div className="w-full max-w-2xl space-y-6">
          {/* Form Header with Login Button */}
          <div className="flex items-start justify-between mb-8">
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                Welcome to NRCS.
              </h2>
              <p className="text-base md:text-lg text-gray-600">
                Blood Transfusion Service Management
              </p>
            </div>
            <button
              onClick={() => (window.location.href = "/login")}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-200 ml-4"
            >
              <LogIn className="h-4 w-4" />
              <span>Login</span>
            </button>
          </div>
          {/* Chapter Selection */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 mb-6">
            <h2 className="text-lg font-semibold text-red-600 mb-4 pb-2 border-b border-gray-100">
              Select BTS Chapter
            </h2>
            <div className="relative">
              <Select
                value={
                  organizations.find((org) => org.id === selectedOrgId) || null
                }
                onChange={handleOrgChange}
                options={organizations}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder={loading ? "Loading..." : "Select BTS Chapter..."}
                isSearchable={true}
                isLoading={loading}
                loadingMessage={() => "Loading chapters..."}
                noOptionsMessage={() => "No chapters found"}
                isClearable={false}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "48px",
                    borderRadius: "0.5rem",
                    borderColor: "#D1D5DB",
                    "&:hover": {
                      borderColor: "#9CA3AF",
                    },
                    boxShadow: "none",
                    "&:focus-within": {
                      boxShadow: "0 0 0 2px rgba(99, 102, 241, 0.5)",
                      borderColor: "#6366F1",
                    },
                  }),
                  option: (base, { isFocused, isSelected }) => ({
                    ...base,
                    backgroundColor: isSelected
                      ? "#6366F1"
                      : isFocused
                      ? "#EEF2FF"
                      : "white",
                    color: isSelected ? "white" : "#1F2937",
                    "&:active": {
                      backgroundColor: "#E0E7FF",
                    },
                  }),
                }}
              />
            </div>
          </div>

          {/* Notices Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 min-h-[200px]">
            <h2 className="text-lg font-semibold text-red-600 mb-4 pb-2 border-b border-gray-100">
              Notices
            </h2>
            {noticesLoading ? (
              <div className="flex items-center justify-center h-full py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
                  <p className="mt-3 text-gray-500">Loading notices...</p>
                </div>
              </div>
            ) : noticeData.length > 0 ? (
              <div className="space-y-3">
                {noticeData.map((notice, index) => (
                  <NoticeCard key={index} notice={notice} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <Bell className="h-6 w-6 text-gray-400" />
                </div>
                <p className="text-gray-500">
                  No notices found for this chapter.
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Please check back later for updates.
                </p>
              </div>
            )}
          </div>

          {/* Upcoming Campaigns Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 min-h-[200px]">
            <h2 className="text-lg font-semibold text-red-600 mb-4 pb-2 border-b border-gray-100">
              Upcoming Campaign
            </h2>
            <div className="grid grid-cols-1 gap-4">
              {upcomingCampaigns && upcomingCampaigns.length > 0 ? (
                upcomingCampaigns.map((campaign, index) => (
                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 p-3"
                  >
                    {/* Header with status */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        {/* <div className="w-2 h-2 rounded-full bg-red-500"></div>
                         */}
                        <Pin className="w-4 h-4 text-gray-400" />
                        <h3 className="font-semibold text-gray-900 text-sm truncate">
                          {campaign.venue && campaign.venue !== "-"
                            ? `${campaign.venue}`
                            : "Blood Donation Campaign"}
                        </h3>
                      </div>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          campaign.docstatus === "C"
                            ? "bg-green-100 text-green-800"
                            : campaign.docstatus === "R"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {campaign.docstatus === "C"
                          ? "Confirmed"
                          : campaign.docstatus === "R"
                          ? "Running"
                          : "Pending"}
                      </span>
                    </div>

                    {/* Content in vertical stack for better fit */}
                    <div className="space-y-2 text-sm">
                      {/* Date */}
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-900 font-medium">
                          {campaign.startdate
                            ? new Date(campaign.startdate).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                }
                              )
                            : "TBD"}
                        </span>
                      </div>

                      {/* Time */}
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-900 font-medium">
                          {campaign.starttime &&
                          campaign.starttime !== "00:01:00"
                            ? campaign.starttime
                            : "TBD"}
                        </span>
                      </div>

                      {/* Venue */}
                      {/* <div className="flex items-center space-x-2">
                        <Building className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600 truncate">
                          {campaign.venue && campaign.venue !== "-"
                            ? campaign.venue
                            : "TBD"}
                        </span>
                      </div> */}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 border border-gray-100 rounded-lg text-center">
                  <p className="text-gray-500 font-medium">
                    No Upcoming Campaigns Available
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    Please check back later for updates.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-gray-100 w-full">
          <p className="text-xs text-center text-gray-500">
            &copy; NRCS. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

export default PublicDashboard;
